import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Calendar,
  Check,
  ChevronDown,
  ClipboardList,
  Eye,
  EyeOff,
  FileText,
  Filter,
  RotateCcw,
  Search,
  User as UserIcon,
  X,
} from 'lucide-react';
import { useLang } from '../../../LangContext';
import { statusColor, isCompletedCode } from '../../../utils/statusColor';
import { statusLabel } from '../../../utils/statusLabel';

// Gönderim listesi ve kanban görünümü arasında paylaşılan filtre paneli:
// arama, tarih aralığı, form/kullanıcı/statü seçimi ve "Tamamlananları göster" anahtarı.
export default function SubmissionFilterBar({
  filters,
  setFilters,
  filtersOpen,
  setFiltersOpen,
  formOptions,
  userOptions,
  statuses,
  activeFilterCount,
  toggleMulti,
  resetFilters,
}) {
  const { locale: t } = useLang();
  const st = t.submissions;
  const hasCompletedStatus = (statuses || []).some((status) => isCompletedCode(status.code));

  return (
    <div className={`sp-filters${filtersOpen ? '' : ' is-collapsed'}`}>
      <div className="sp-filters-head">
        <button type="button" className="sp-filters-toggle" onClick={() => setFiltersOpen((open) => !open)}>
          <Filter size={16} /> {st.filtersLabel}
          {activeFilterCount > 0 && <span className="sp-filter-count">{activeFilterCount}</span>}
        </button>
        {activeFilterCount > 0 && (
          <button type="button" className="sp-filter-reset" onClick={resetFilters} title="Filtreleri temizle">
            <RotateCcw size={14} /> {st.clearFilters}
          </button>
        )}
      </div>

      {filtersOpen && (
        <div className="sp-filters-body">
          <div className="sp-filter-row">
            <label className="sp-filter-label sp-filter-search">
              <span><Search size={14} /> {st.searchLabel}</span>
              <input
                type="text"
                className="sp-filter-input"
                placeholder={st.searchPlaceholder}
                value={filters.search}
                onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              />
            </label>
            <label className="sp-filter-label">
              <span><Calendar size={14} /> {st.dateFrom}</span>
              <input
                type="date"
                className="sp-filter-input"
                value={filters.dateFrom}
                onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
              />
            </label>
            <label className="sp-filter-label">
              <span><Calendar size={14} /> {st.dateTo}</span>
              <input
                type="date"
                className="sp-filter-input"
                value={filters.dateTo}
                onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
              />
            </label>
          </div>

          <div className="sp-filter-row sp-filter-row-groups">
            <div className="sp-filter-group">
              <div className="sp-filter-group-title"><FileText size={14} /> {st.formsGroup}</div>
              <MultiSelectDropdown
                placeholder={st.selectPlaceholder}
                emptyLabel={st.noFormOptions}
                options={formOptions}
                selectedIds={filters.formIds}
                onToggle={(id) => toggleMulti('formIds', id)}
                onClear={() => setFilters((prev) => ({ ...prev, formIds: [] }))}
              />
            </div>

            <div className="sp-filter-group">
              <div className="sp-filter-group-title"><UserIcon size={14} /> {st.usersGroup}</div>
              <MultiSelectDropdown
                placeholder={st.userSelectPlaceholder}
                emptyLabel={st.noUserOptions}
                options={userOptions}
                selectedIds={filters.userIds}
                onToggle={(id) => toggleMulti('userIds', id)}
                onClear={() => setFilters((prev) => ({ ...prev, userIds: [] }))}
              />
            </div>

            <div className="sp-filter-group">
              <div className="sp-filter-group-title"><ClipboardList size={14} /> {st.statusesGroup}</div>
              <div className="sp-chip-row">
                {(statuses || []).map((status) => {
                  const active = filters.statusIds.includes(status.id);
                  const color = statusColor(status.code);
                  return (
                    <button
                      type="button"
                      key={status.id}
                      className={`sp-chip sp-chip-status${active ? ' is-active' : ''}`}
                      style={active ? { background: color.bg, borderColor: color.border, color: color.text } : undefined}
                      onClick={() => toggleMulti('statusIds', status.id)}
                    >
                      <span className="sp-chip-dot" style={{ background: color.text }} />
                      {statusLabel(t, status.code, status.name)}
                      {active && <X size={12} />}
                    </button>
                  );
                })}
              </div>
            </div>

            {hasCompletedStatus && (
              <div className="sp-filter-group">
                <div className="sp-filter-group-title">{filters.showCompleted ? <Eye size={14} /> : <EyeOff size={14} />} {st.completedToggleGroup}</div>
                <button
                  type="button"
                  className={`sp-chip sp-chip-toggle${filters.showCompleted ? ' is-active' : ''}`}
                  onClick={() => setFilters((prev) => ({ ...prev, showCompleted: !prev.showCompleted }))}
                >
                  {filters.showCompleted ? <Check size={12} /> : null} {st.showCompleted}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ----- Çoklu seçim dropdown bileşeni (form / kullanıcı filtreleri için) -----
function MultiSelectDropdown({ options, selectedIds, onToggle, onClear, placeholder, emptyLabel }) {
  const { locale: t } = useLang();
  const ms = t.submissions;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  // Dış tıklamada paneli kapat
  useEffect(() => {
    if (!open) return undefined;
    function handleClick(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => (opt.name || '').toLowerCase().includes(q));
  }, [options, query]);

  const selectedCount = selectedIds.length;
  const previewName = selectedCount === 1
    ? options.find((opt) => opt.id === selectedIds[0])?.name
    : null;

  return (
    <div className="sp-ms" ref={containerRef}>
      <button
        type="button"
        className={`sp-ms-trigger${selectedCount > 0 ? ' is-active' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="sp-ms-trigger-label">
          {selectedCount === 0 && <span className="sp-ms-placeholder">{placeholder}</span>}
          {selectedCount === 1 && <span className="sp-ms-selected-single">{previewName}</span>}
          {selectedCount > 1 && (
            <span className="sp-ms-selected-multi">
              <span className="sp-ms-badge">{selectedCount}</span> {placeholder}
            </span>
          )}
        </span>
        {selectedCount > 0 && (
          <span
            role="button"
            tabIndex={0}
            className="sp-ms-clear"
            onClick={(event) => { event.stopPropagation(); onClear(); }}
            onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onClear(); } }}
            title={ms.clearSelection}
          >
            <X size={12} />
          </span>
        )}
        <ChevronDown size={14} className={`sp-ms-chevron${open ? ' is-open' : ''}`} />
      </button>

      {open && (
        <div className="sp-ms-panel" role="listbox">
          <div className="sp-ms-search">
            <Search size={13} />
            <input
              type="text"
              autoFocus
              value={query}
              placeholder={ms.searchInDropdown}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <div className="sp-ms-list">
            {filtered.length === 0 && <div className="sp-ms-empty">{query ? ms.noDropdownResults : emptyLabel}</div>}
            {filtered.map((opt) => {
              const active = selectedIds.includes(opt.id);
              return (
                <button
                  type="button"
                  key={opt.id}
                  className={`sp-ms-option${active ? ' is-active' : ''}`}
                  onClick={() => onToggle(opt.id)}
                >
                  <span className={`sp-ms-check${active ? ' is-active' : ''}`}>
                    {active && <Check size={12} />}
                  </span>
                  <span className="sp-ms-option-label" title={opt.name}>{opt.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
